import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Leaf, BookOpen, CalendarCheck, Plus } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const PHASE_LABELS = { germination: 'Keimung', seedling: 'Sämling', vegetative: 'Vegetativ', flowering: 'Blüte', harvest: 'Ernte' };
const DEFAULT_COVER = 'https://images.unsplash.com/photo-1707055039188-b95a442223dd?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800';

export default function ProjectDetailPage() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [plants, setPlants] = useState([]);
  const [diary, setDiary] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [pRes, plRes, dRes] = await Promise.all([
          axios.get(`${API}/api/projects/${id}`),
          axios.get(`${API}/api/plants?project_id=${id}`),
          axios.get(`${API}/api/diary?project_id=${id}`),
        ]);
        setProject(pRes.data);
        setPlants(Array.isArray(plRes.data) ? plRes.data : []);
        setDiary((Array.isArray(dRes.data) ? dRes.data : []).slice(0, 5));
      } catch { setPlants([]); setDiary([]); }
      setLoading(false);
    };
    load();
  }, [id]);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!project) return (
    <div className="p-6 text-center">
      <p className="text-emerald-100/50">Projekt nicht gefunden</p>
      <Link to="/projects" className="text-emerald-400 mt-2 inline-block">Zurück</Link>
    </div>
  );

  return (
    <div className="animate-fade-in" data-testid="project-detail">
      {/* Hero */}
      <div className="relative h-48 md:h-64 overflow-hidden">
        <img src={project.cover_image || DEFAULT_COVER} alt={project.name}
          className="w-full h-full object-cover" onError={e => { e.target.src = DEFAULT_COVER; }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, #050A07 0%, rgba(5,10,7,0.4) 100%)' }} />
        <div className="absolute top-4 left-4">
          <Link to="/projects" className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-white/80 hover:text-white transition-colors"
            style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}>
            <ArrowLeft size={16} /> Zurück
          </Link>
        </div>
        <div className="absolute bottom-4 left-4 right-4">
          <h1 className="font-heading text-2xl font-bold text-white">{project.name}</h1>
          {project.description && <p className="text-white/60 text-sm mt-1">{project.description}</p>}
        </div>
      </div>

      <div className="p-4 md:p-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="card-grow p-3 text-center">
            <p className="font-heading text-xl font-bold text-emerald-50">{plants.length}</p>
            <p className="text-xs text-emerald-100/50">Pflanzen</p>
          </div>
          <div className="card-grow p-3 text-center">
            <p className="font-heading text-xl font-bold text-emerald-50">{diary.length}</p>
            <p className="text-xs text-emerald-100/50">Tagebuch</p>
          </div>
          <div className="card-grow p-3 text-center">
            <p className={`font-heading text-sm font-bold ${project.status === 'active' ? 'text-emerald-400' : 'text-yellow-400'}`}>
              {project.status === 'active' ? 'Aktiv' : project.status}
            </p>
            <p className="text-xs text-emerald-100/50">Status</p>
          </div>
        </div>

        {/* Plants */}
        <div className="card-grow p-4" data-testid="project-plants">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading font-semibold text-emerald-100 flex items-center gap-2">
              <Leaf size={16} className="text-emerald-400" /> Pflanzen ({plants.length})
            </h2>
            <Link to={`/plants?project=${id}`} className="text-sm text-emerald-400 hover:text-emerald-300 flex items-center gap-1">
              <Plus size={14} /> Pflanze hinzufügen
            </Link>
          </div>
          {plants.length === 0 ? (
            <p className="text-sm text-emerald-100/30 text-center py-4">Noch keine Pflanzen</p>
          ) : (
            <div className="space-y-2">
              {plants.map(p => (
                <div key={p.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#050A07] transition-colors" style={{ border: '1px solid #1B2A21' }}>
                  <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0">
                    {p.image ? (
                      <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center" style={{ background: 'rgba(16,185,129,0.1)' }}>
                        <Leaf size={14} className="text-emerald-400" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-emerald-100 truncate">{p.name}</p>
                  </div>
                  <span className={`badge badge-green text-xs phase-${p.phase}`}>{PHASE_LABELS[p.phase] || p.phase}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Diary */}
        <div className="card-grow p-4" data-testid="project-diary">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading font-semibold text-emerald-100 flex items-center gap-2">
              <BookOpen size={16} className="text-emerald-400" /> Tagebuch
            </h2>
            <Link to={`/diary?project=${id}`} className="text-sm text-emerald-400 hover:text-emerald-300">
              Alle ansehen
            </Link>
          </div>
          {diary.length === 0 ? (
            <p className="text-sm text-emerald-100/30 text-center py-4">Noch keine Einträge</p>
          ) : (
            <div className="space-y-3">
              {diary.map(entry => (
                <div key={entry.id} className="py-2 border-b last:border-0" style={{ borderColor: '#1B2A21' }}>
                  <p className="font-medium text-sm text-emerald-100">{entry.title}</p>
                  <p className="text-xs text-emerald-100/50 mt-1 line-clamp-2">{entry.content}</p>
                  <p className="text-xs text-emerald-100/30 mt-1">{new Date(entry.created_at).toLocaleDateString('de-DE')}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-3">
          <Link to={`/care-log?project=${id}`} className="card-grow p-4 flex items-center gap-3 hover:border-emerald-500/30 transition-colors">
            <CalendarCheck size={20} className="text-emerald-400 flex-shrink-0" />
            <span className="text-sm font-medium text-emerald-100">Pflege eintragen</span>
          </Link>
          <Link to={`/diary?project=${id}`} className="card-grow p-4 flex items-center gap-3 hover:border-emerald-500/30 transition-colors">
            <BookOpen size={20} className="text-emerald-400 flex-shrink-0" />
            <span className="text-sm font-medium text-emerald-100">Tagebucheintrag</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
